import { useCallback, useEffect, useState } from "react";
import { Plus, Save, Send, Trash2, FileText, FileCode } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TemplateEditor } from "./TemplateEditor";

const BLANK = { name: "", subject: "", body: "", format: "text" };
const isHtmlBody = (b) => /<(p|div|br|table|h[1-6]|ul|ol|a|strong|b|em|span)\b/i.test(b);

export function TemplatesPanel({ slot, project, onProjectChange }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [testTo, setTestTo] = useState(user?.email || "");

  const load = useCallback(() => api.get(`/projects/${slot}/templates`).then((r) => setList(r.data)), [slot]);
  useEffect(() => { load(); }, [load]);

  const open = (t) => { setActiveId(t?.id || null); setForm(t ? { name: t.name, subject: t.subject, body: t.body, format: t.format || (isHtmlBody(t.body) ? "html" : "text") } : BLANK); };

  const save = async () => {
    try {
      const { data } = activeId
        ? await api.put(`/projects/${slot}/templates/${activeId}`, form)
        : await api.post(`/projects/${slot}/templates`, form);
      setActiveId(data.id);
      toast.success("Template saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/projects/${slot}/templates/${activeId}`);
    open(null);
    load();
  };

  const test = async () => {
    try {
      await api.post(`/projects/${slot}/templates/${activeId}/test`, { to: testTo });
      toast.success(`Test email sent to ${testTo}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-6" data-testid="templates-panel">
      <aside className="space-y-2">
        <Button onClick={() => open(null)} variant="secondary" className="w-full justify-start" data-testid="template-new-button"><Plus className="h-4 w-4 mr-2" />New template</Button>
        {list.map((t) => (
          <button key={t.id} onClick={() => open(t)} data-testid={`template-item-${t.id}`}
            className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${activeId === t.id ? "border-primary bg-primary/10" : "border-border bg-card hover:bg-secondary/60"}`}>
            <p className="text-sm font-medium truncate flex items-center gap-2">
              {(t.format || (isHtmlBody(t.body) ? "html" : "text")) === "html" ? <FileCode className="h-3.5 w-3.5 text-primary shrink-0" /> : <FileText className="h-3.5 w-3.5 text-primary shrink-0" />}
              <span className="truncate">{t.name}</span>
            </p>
            <p className="text-xs text-muted-foreground truncate mt-0.5">{t.subject}</p>
          </button>
        ))}
        {list.length === 0 && <p className="text-xs text-muted-foreground px-1 pt-2">No templates in this project yet.</p>}
      </aside>
      <section className="rounded-xl border border-border bg-card p-6 space-y-5">
        <TemplateEditor form={form} setForm={setForm} slot={slot} project={project} onProjectChange={onProjectChange} />
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border">
          <Button onClick={save} disabled={!form.name || !form.subject || !form.body} data-testid="template-save-button"><Save className="h-4 w-4 mr-1.5" />Save</Button>
          {activeId && <Button variant="ghost" onClick={remove} className="text-rose-700" data-testid="template-delete-button"><Trash2 className="h-4 w-4 mr-1.5" />Delete</Button>}
          {activeId && (
            <div className="flex items-center gap-2 ml-auto">
              <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="w-64" placeholder="test@yourdomain.com" data-testid="template-test-email-input" />
              <Button variant="secondary" onClick={test} disabled={!testTo} data-testid="campaign-send-test-button"><Send className="h-4 w-4 mr-1.5" />Send test</Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
